import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { FieldArray, Formik } from "formik";
import { useCreateDisciplineMutation, useGetDisciplineDataQuery, useUpdateDisciplineMutation } from "store/services/disciplines";
import AppInputField from "components/shared/FormComponents/AppInputField";
import LoadingButton from "components/shared/LoadingButton";
import { successMessageToast } from "helpers/common";
import { useNavigate, useParams, Link } from "react-router-dom";
import disciplineFormSchema from "./DisciplineForm.schema";
import { DISCIPLINES_LIST } from "config/paths";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { MinusIcon, PlusIcon } from "Assets/icons";

// css
import "react-phone-input-2/lib/style.css";
import styles from "./addForm.module.scss";
import ErrorComponent from "components/shared/ErrorComponent";
import useAutoUpdateInitialValues from "hooks/useAutoUpdateInitialValues";
import { RequiredFieldAstrick } from "components/Common/Common";

const AddDiscipline = ({ }) => {
  const { id: disciplineId } = useParams();
  const editMode = !!disciplineId;
  const navigate = useNavigate();

  const { data: disciplineData, isLoading: getDisciplineLoading, isError: getDisciplineError } = useGetDisciplineDataQuery(disciplineId, { skip: !disciplineId });
  const [createDiscipline, { isLoading }] = useCreateDisciplineMutation();
  const [updateDiscipline, { isLoading: updateDisciplineLoading }] = useUpdateDisciplineMutation();

  const [initialValues] = useAutoUpdateInitialValues(defaultInitialValues, [disciplineData]);

  const handleClickCancel = () => {
    navigate(DISCIPLINES_LIST);
  }

  const handleSubmit = async (values) => {
    try {
      let disciplineAreas = values.discipline_areas;
      if (!editMode) {
        disciplineAreas =
          values.discipline_areas
            .map((area) => area.name)
            .filter(areaName => !!areaName);
      }
      let payload = {
        name: values.name,
        assign_term: values.assign_term,
        discipline_areas: disciplineAreas,
        status: "active"
      }

      let result;
      if (editMode) {
        result = await updateDiscipline({
          id: disciplineId,
          payload
        });
      } else {
        result = await createDiscipline(payload);
      }
      if (result.error) {
        console.error("Error creating discipline", result.error);
      } else {
        successMessageToast(result.data?.message || "Discipline created successfully");
        navigate(DISCIPLINES_LIST);
      }
    } catch (err) {
      console.log("Error creating discipline", err);
    }
  }

  return (
    <>
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead d-flex align-items-center flex-wrap">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to="/manage-disciplines/discipline-profiles/"
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit" : "Create New"} Discipline</h6>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              {getDisciplineLoading ? (
                <LoadingIndicator />
              ) : getDisciplineError ? (
                <ErrorComponent />
              ) : (
                <>
                  {console.log("initialValues", initialValues)}
                  <Formik
                    initialValues={{
                      ...initialValues,
                      discipline_areas: initialValues?.discipline_areas?.length ? initialValues?.discipline_areas : [{ name: "" }]
                    }}
                    validationSchema={disciplineFormSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({
                      handleSubmit,
                      values,
                      errors,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          <Row>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Discipline Name
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="name"
                                value={values?.name}
                                placeholder="i.e. Speech-Language Pathologist"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Assign Term
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="assign_term"
                                value={values?.assign_term}
                                placeholder="i.e. SLP"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col lg="12" className="my-2">
                              <Row>
                                <Col md="4" sm="6" className="my-2">
                                  <label
                                    htmlFor=""
                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                  >
                                    Add New Area
                                    <RequiredFieldAstrick />
                                  </label>
                                  <FieldArray name="discipline_areas">
                                    {({ insert, remove, push }) => (
                                      <ul className="list-unstyled ps-0 mb-0">
                                        {values.discipline_areas.length > 0 &&
                                          values.discipline_areas.map((area, index) => (
                                            <DisciplineAreaItem
                                              key={index}
                                              index={index}
                                              isLastItem={index === values.discipline_areas.length - 1}
                                              insert={insert}
                                              remove={remove}
                                              push={push}
                                              errors={errors}
                                              values={values?.discipline_areas || []}
                                            />
                                          ))}
                                      </ul>
                                    )}
                                  </FieldArray>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </div>
                        <Row className="mt-4">
                          <Col className="my-2" lg="12">
                            <div
                              className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                              style={{ borderTop: "1px solid #41a2a84d" }}
                            >
                              <LoadingButton
                                type="submit"
                                className="d-flex align-items-center justify-content-center commonBtn"
                                isLoading={isLoading || updateDisciplineLoading}
                              >
                                Save
                              </LoadingButton>
                              <Button
                                className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                onClick={handleClickCancel}
                              >
                                Cancel
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

const DisciplineAreaItem = ({ name, index, isLastItem, remove, push, values }) => {
  return (
    <li key={index}>
      <div className="py-1 d-flex align-items-center gap-10">
        <AppInputField
          name={`discipline_areas.${index}.name`}
          placeholder="i.e. Phonological Awareness"
          className={`${styles.formControl} form-control`}
        />
        <Button
          className="border-0 p-0"
          variant="transparent"
        // onClick={() => isLastItem ? push({ id: null, name: "" }) : remove(index)}
        >
          {isLastItem ? (

            values?.length > 1 ? (
              <div className="d-flex w-100">
                <PlusIcon onClick={() => push({ id: null, name: "" })} />
                <MinusIcon onClick={() => remove(index)} />
              </div>
            ) : <PlusIcon onClick={() => push({ id: null, name: "" })} />

          ) : <MinusIcon onClick={() => remove(index)} />
          }
        </Button>
      </div>
    </li>
  );
};

const defaultInitialValues = {
  name: "",
  assign_term: "",
  discipline_areas: [{
    name: ""
  }]
}

export default AddDiscipline;

